// src/components/sections/home/Testimonials.tsx
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Testimonial } from '../../../types/TeamMember';

// Testimonials data integrated directly into the component
const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Ana Horvat',
    role: 'Šefinja kuhinje, Restavracija Zeleni Gaj',
    content: 'Bučno olje Oljarne Krajnc uporabljam v svoji kuhinji že več let. Njegova bogata aroma in izjemna kakovost sta nepogrešljivi pri pripravi tradicionalnih slovenskih jedi. Vsaka kapljica tega olja prinaša pristno doživetje okusov.',
    image: '/assets/images/domov/testimonial/ana-horvat.jpg'
  },
  {
    id: '2',
    name: 'Matjaž Berič',
    role: 'Gurman in bloger',
    content: 'Kot ljubitelj dobre hrane sem preizkusil številna bučna olja, a tisto iz Oljarne Krajnc je resnično posebno. Njihov tradicionalni postopek pridelave ustvarja olje z bogatim okusom, ki obogati vsako solato ali jed.',
    image: '/assets/images/domov/testimonial/matjaz-beric.jpg'
  },
  {
    id: '3',
    name: 'Jožica Novak',
    role: 'Redna stranka',
    content: 'Že vrsto let kupujem izdelke Oljarne Krajnc. Njihovo bučno olje ima posebno mesto v moji kuhinji - ne le zaradi izjemnega okusa, temveč tudi zaradi zavezanosti tradiciji in kakovosti, ki jo čutim v vsakem izdelku.',
    image: '/assets/images/domov/testimonial/jozica-novak.jpg'
  },
  {
    id: '4',
    name: 'Peter Kovačič',
    role: 'Kuhar in predavatelj',
    content: 'Pri poučevanju kulinarike vedno poudarjam pomen kakovostnih sestavin. Bučno olje Oljarne Krajnc je odličen primer, kako tradicionalni postopki ustvarjajo vrhunske izdelke, ki dvignejo vsako jed na višji nivo.',
    image: '/assets/images/domov/testimonial/peter-kovacic.jpg'
  }
];

const TestimonialsSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const nextTestimonial = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };
  
  const prevTestimonial = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };
  
  // Auto-advance testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <section className="bg-white py-12 md:py-16">
      <div className="container-wide">
        <div className="section-title">
          <h2>Kaj pravijo naše stranke</h2>
          <div className="divider"></div>
        </div>
        
        <div className="relative mt-20">
          {/* Large quote icon - positioned higher */}
          <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 z-10">
            <Quote size={64} className="text-secondary" />
          </div>
          
          <div className="bg-primary-600 rounded-lg p-8 pt-20 shadow-lg transition-all duration-300 hover:shadow-xl">
            <div className="relative max-w-4xl mx-auto">
              {/* Testimonial slider */}
              <div className="overflow-hidden relative">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {testimonials.map((testimonial) => (
                    <div 
                      key={testimonial.id} 
                      className="w-full flex-shrink-0 text-center px-6"
                    >
                      <p className="text-lg md:text-xl italic mb-8 text-white">
                        "{testimonial.content}"
                      </p>
                      
                      <div className="flex items-center justify-center">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name} 
                          className="w-20 h-20 rounded-full object-cover mr-4 border-2 border-secondary"
                          width={100}
                          height={100}
                          loading="eager"
                        />
                        <div className="text-left">
                          <h4 className="font-bold text-lg text-white">
                            {testimonial.name}
                          </h4>
                          {testimonial.role && (
                            <p className="text-neutral text-sm">
                              {testimonial.role}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Navigation arrows */}
                <button 
                  onClick={prevTestimonial}
                  className="absolute top-1/2 left-0 transform -translate-y-1/2 bg-primary-500 hover:bg-primary-800 transition-colors rounded-full p-2 text-white"
                  aria-label="Prejšnje mnenje"
                >
                  <ChevronLeft size={24} />
                </button>
                
                <button 
                  onClick={nextTestimonial}
                  className="absolute top-1/2 right-0 transform -translate-y-1/2 bg-primary-500 hover:bg-primary-800 transition-colors rounded-full p-2 text-white"
                  aria-label="Naslednje mnenje"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
              
              {/* Dots indicator */}
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (!isTransitioning) {
                        setIsTransitioning(true);
                        setCurrentIndex(index);
                        setTimeout(() => {
                          setIsTransitioning(false);
                        }, 500);
                      }
                    }}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      currentIndex === index ? 'bg-secondary' : 'bg-primary-500'
                    }`}
                    aria-label={`Mnenje ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;